import { FC } from 'react';
import { NetWorthChartInner, NetWorthChartInnerProps } from './NetWorthChartInner';
import { ChartContainerProps, ChartContainer } from './ChartContainer';
import { Authentication } from '../../environment';
import { monetaryAmountValueInCurrencyMajorUnits } from '../../util/dataPoints.util';
import { totalMonetaryAmountFromAssets } from '../../util/assets';
import { Assets } from '../../util/types';
import { useTtDetails } from '../../TtDetails';
import { separateByEstateNature } from '../../RealEstateProperty/util';
import { totalValueOfDebts, useGraphqlClient } from '../..';
import { ClientOnly } from 'remix-utils/client-only';

export type AuthenticationWithBaseUrl = Authentication & {
  baseUrl: string;
};

export type FirmColor = {
  firmColor: string;
  firmColorIsLight: boolean;
  logoURL: string | undefined;
};

interface NetWorthChartProps
  extends Omit<NetWorthChartInnerProps, 'gqlClient'>,
    Pick<ChartContainerProps, 'defaultRangeFilter' | 'defaultChartRangeOption'> {
  firmColor: FirmColor;
}

export const NetWorthChart: FC<NetWorthChartProps> = ({
  clientID,
  householdID,
  defaultRangeFilter,
  defaultChartRangeOption,
  firmColor,
}) => {
  const gqlClient = useGraphqlClient();
  const { ttDetails } = useTtDetails({ householdID, includeDebtSummary: true, includeAssetSummary: true });
  const debtSummary = ttDetails?.debtSummary;
  const loans = debtSummary
    ? [
        ...debtSummary.studentLoans,
        ...debtSummary.businessLoans,
        ...debtSummary.investmentRealEstateLoans,
        ...debtSummary.personalRealEstateLoans,
        ...debtSummary.otherDebt,
      ]
    : [];

  let assets: Assets = {};
  const assetSummary = ttDetails?.assetSummary;

  if (assetSummary) {
    const { business } = separateByEstateNature([
      ...assetSummary.personalRealEstate,
      ...assetSummary.investmentRealEstate,
    ]);
    assets = {
      cashAccounts: assetSummary.cashAccounts ?? [],
      postTaxInvestments: assetSummary.afterTaxInvestments,
      preTaxInvestments: assetSummary.preTaxInvestments,
      personalRealEstate: assetSummary.personalRealEstate,
      businesses: assetSummary.businesses ?? [],
      businessRealEstate: business,
      personalPropertyItems: assetSummary.personalPropertyItems ?? [],
      noteReceivables: assetSummary.noteReceivables ?? [],
    };
  }

  const netWorthMajorUnits = monetaryAmountValueInCurrencyMajorUnits({
    value: totalMonetaryAmountFromAssets(assets).value + totalValueOfDebts(loans).value,
    currencyCode: 'USD',
  });

  return (
    <ClientOnly>
      {() => (
        <div className="w-[inherit]">
          <ChartContainer
            ownerID={clientID}
            defaultRangeFilter={defaultRangeFilter}
            defaultChartRangeOption={defaultChartRangeOption}
          >
            <div className="mb-[23px] grid w-max grid-flow-col items-center gap-6">
              <span className="text-[28px] font-bold leading-7 text-white">Net Worth Progress</span>
            </div>
            <p
              style={{
                marginTop: '5px',
                marginBottom: '11px',
                fontSize: '56px',
                letterSpacing: '0.45px',
                height: '48px',
              }}
              className="leading-none"
            >
              <span className="font-medium text-darkMode-gray-light">{netWorthMajorUnits < 0 ? '-$' : '$'}</span>
              <span className="font-extrabold text-white">
                {Math.abs(netWorthMajorUnits).toLocaleString('en', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </span>
            </p>
            <NetWorthChartInner
              clientID={clientID}
              householdID={householdID}
              gqlClient={gqlClient}
              firmColor={firmColor}
            />
          </ChartContainer>
        </div>
      )}
    </ClientOnly>
  );
};
